import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Camera, Layers, CheckCircle2, Clock, RefreshCw, Trash2,
  FileText, MessageSquare, HardDrive, Calendar, Award, AlertCircle, Users,
  ExternalLink, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  Legend, AreaChart, Area, CartesianGrid,
} from 'recharts';
import CountUp from 'react-countup';
import ProjectIcon from '../components/ProjectIcon';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  'Beklemede': '#9CA3AF',
  'İşlemde': '#3B82F6',
  'İncelemede': '#F59E0B',
  'Tamamlandı': '#10B981',
};

const PRIORITY_COLORS = {
  acil: '#EF4444', urgent: '#EF4444',
  yuksek: '#F97316', 'yüksek': '#F97316', high: '#F97316',
  orta: '#F59E0B', medium: '#F59E0B', normal: '#F59E0B',
  dusuk: '#10B981', 'düşük': '#10B981', low: '#10B981',
};

const PRIORITY_LABELS = {
  acil: 'Acil', urgent: 'Acil',
  yuksek: 'Yüksek', 'yüksek': 'Yüksek', high: 'Yüksek',
  orta: 'Orta', medium: 'Orta', normal: 'Orta',
  dusuk: 'Düşük', 'düşük': 'Düşük', low: 'Düşük',
};

export default function ProjectSummary({ projectIdProp, snapshotData = null }) {
  const params = useParams();
  const projectId = projectIdProp || params.projectId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(snapshotData);
  const [loading, setLoading] = useState(!snapshotData);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [creating, setCreating] = useState(false);
  const [filterTagId, setFilterTagId] = useState('all');
  const isReadOnly = !!snapshotData;

  useEffect(() => {
    if (snapshotData || !projectId) return;
    fetchSummary();
  }, [projectId, snapshotData, filterTagId]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const url = filterTagId && filterTagId !== 'all'
        ? `${API}/projects/${projectId}/summary?tag_id=${encodeURIComponent(filterTagId)}`
        : `${API}/projects/${projectId}/summary`;
      const r = await axios.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Özet yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchSnapshots = async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/snapshots`);
      setSnapshots(r.data || []);
    } catch (e) {
      toast.error('Snapshot listesi yüklenemedi');
    }
  };

  const handleOpenSnapshots = async () => {
    await fetchSnapshots();
    setShowSnapshots(true);
  };

  const handleDeleteSnapshot = async (snapshotId) => {
    if (!window.confirm('Bu snapshot kalıcı olarak silinecek. Devam etmek istiyor musunuz?')) return;
    try {
      await axios.delete(`${API}/snapshots/${snapshotId}`);
      toast.success('Snapshot silindi');
      await fetchSnapshots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Snapshot silinemedi');
    }
  };

  const handleCreateSnapshot = async () => {
    setCreating(true);
    try {
      const url = filterTagId && filterTagId !== 'all'
        ? `${API}/projects/${projectId}/snapshots?tag_id=${encodeURIComponent(filterTagId)}`
        : `${API}/projects/${projectId}/snapshots`;
      const r = await axios.post(url);
      toast.success(
        r.data.appliedTagName
          ? `Snapshot oluşturuldu (Kategori: ${r.data.appliedTagName})`
          : 'Snapshot başarıyla oluşturuldu'
      );
      await fetchSnapshots();
      // Yeni pencerede aç
      window.open(r.data.viewUrl, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Snapshot oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const url = filterTagId && filterTagId !== 'all'
        ? `${API}/projects/${projectId}/summary-pdf?tag_id=${encodeURIComponent(filterTagId)}`
        : `${API}/projects/${projectId}/summary-pdf`;
      const r = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      // Filtre varsa dosya adına ekle
      const tagSuffix = filterTagId !== 'all' && data.available_tags
        ? `_${(data.available_tags.find(t => t.id === filterTagId) || {}).name || ''}`
        : '';
      a.download = `${data.project.name}${tagSuffix}_ozet.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error('PDF indirilemedi');
    }
  };

  if (loading || !data) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const m = data.summary_metrics;
  const statusData = Object.entries(data.status_distribution || {}).map(([k, v]) => ({ name: k, value: v }));
  const priorityData = Object.entries(data.priority_distribution || {}).map(([k, v]) => ({
    name: PRIORITY_LABELS[k] || k,
    value: v,
    fill: PRIORITY_COLORS[k] || '#6B7280',
  }));
  const teamData = (data.team_performance || []).slice(0, 10).map(p => ({
    name: p.name.length > 18 ? p.name.slice(0, 18) + '…' : p.name,
    'Üretim Görevleri': p.production_completed || 0,
  }));
  const timelineData = (data.timeline || []).map(t => ({
    date: new Date(t.date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' }),
    dateFull: new Date(t.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }),
    'Oluşturulan (Kümül)': t.cumCreated,
    'Tamamlanan (Kümül)': t.cumCompleted,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          {!isReadOnly && (
            <button
              onClick={() => navigate('/projects')}
              className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-2 text-sm"
              data-testid="back-to-projects"
            >
              <ArrowLeft className="w-4 h-4" /> Projeler
            </button>
          )}
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 flex items-center gap-3">
            <ProjectIcon project={data.project} size="lg" withRing />
            {data.project.name}
          </h1>
          {data.project.description && (
            <p className="text-gray-600 mt-2 max-w-3xl">{data.project.description}</p>
          )}
          {isReadOnly && data.createdAt && (
            <p className="text-xs text-gray-500 mt-1">
              📸 Snapshot — {new Date(data.createdAt).toLocaleString('tr-TR')} tarihinde {data.createdByName} tarafından oluşturuldu
            </p>
          )}
          {/* Uygulanan Kategori Filtresi Rozeti */}
          {data.applied_tag && (
            <div className="mt-2">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md text-white"
                style={{ backgroundColor: data.applied_tag.color || '#06b6d4' }}
                data-testid="applied-tag-badge"
              >
                <Layers className="w-3 h-3" />
                Kategori Filtresi: {data.applied_tag.name}
              </span>
            </div>
          )}
        </div>
        {!isReadOnly && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Kategori Filtresi — PDF butonunun soluna */}
            {Array.isArray(data.available_tags) && data.available_tags.length > 0 && (
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 shadow-sm">
                <Layers className="w-4 h-4 text-gray-500" />
                <label htmlFor="project-summary-tag-filter" className="text-sm text-gray-600 hidden sm:inline">
                  Kategori:
                </label>
                <select
                  id="project-summary-tag-filter"
                  data-testid="project-summary-tag-filter"
                  value={filterTagId}
                  onChange={(e) => setFilterTagId(e.target.value)}
                  className="bg-transparent text-sm font-medium text-gray-900 focus:outline-none cursor-pointer pr-1"
                  disabled={loading}
                >
                  <option value="all">Tümü</option>
                  {data.available_tags.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {filterTagId !== 'all' && (
                  <button
                    type="button"
                    onClick={() => setFilterTagId('all')}
                    className="text-gray-400 hover:text-gray-600"
                    title="Filtreyi temizle"
                    data-testid="project-summary-tag-filter-clear"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
            <Button
              onClick={handleDownloadPdf}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" /> PDF İndir
            </Button>
            <Button
              onClick={handleOpenSnapshots}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              data-testid="snapshots-btn"
            >
              <Camera className="w-4 h-4 mr-2" /> Snapshots
            </Button>
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Toplam Görev" value={m.total_tasks} icon={Layers} gradient="from-cyan-500 to-blue-500" />
        <KpiCard label="Tamamlanma" value={m.completion_rate} suffix="%" icon={CheckCircle2} gradient="from-green-500 to-emerald-500" />
        <KpiCard label="Takım Üyesi" value={m.team_members} icon={Users} gradient="from-purple-500 to-indigo-500" />
        <KpiCard label="Proje Süresi" value={m.duration_days} suffix=" gün" icon={Calendar} gradient="from-orange-500 to-red-500" />
      </div>

      {/* Detailed Metrics Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <MiniMetric label="İşlemde" value={m.in_progress_tasks} icon={Clock} color="text-blue-600" />
        <MiniMetric label="İncelemede" value={m.in_review_tasks} icon={RefreshCw} color="text-amber-600" />
        <MiniMetric label="Beklemede" value={m.pending_tasks} icon={Clock} color="text-gray-600" />
        <MiniMetric label="Revizyon" value={m.total_revisions} icon={AlertCircle} color="text-orange-600" />
        <MiniMetric label="Dosya" value={m.total_files} icon={FileText} color="text-indigo-600" />
        <MiniMetric label="Yorum" value={m.total_comments} icon={MessageSquare} color="text-cyan-600" />
      </div>

      {/* Timeline — Proje takvimi (Gantt yerine) */}
      <ChartCard title="Zaman Çizelgesi" subtitle="Oluşturulan ve tamamlanan görevlerin kümülatif gelişimi" className="mb-6">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={timelineData}>
            <defs>
              <linearGradient id="created" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="completed" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={20} />
            <YAxis />
            <Tooltip
              labelFormatter={(label, payload) => {
                const p = Array.isArray(payload) && payload.length ? payload[0]?.payload : null;
                return p?.dateFull || label;
              }}
            />
            <Legend />
            <Area type="monotone" dataKey="Oluşturulan (Kümül)" stroke="#3B82F6" fill="url(#created)" strokeWidth={2} animationDuration={1500} />
            <Area type="monotone" dataKey="Tamamlanan (Kümül)" stroke="#10B981" fill="url(#completed)" strokeWidth={2} animationDuration={1500} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Görev Durum Dağılımı" subtitle="Beklemede / İşlemde / İncelemede / Tamamlandı">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%" cy="50%" outerRadius={90}
                dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                animationDuration={1000}
              >
                {statusData.map((e, i) => (<Cell key={i} fill={STATUS_COLORS[e.name] || '#6B7280'} />))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Öncelik Dağılımı" subtitle="Görevlerin öncelik durumu">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={priorityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" radius={[8, 8, 0, 0]} animationDuration={1000}>
                {priorityData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>


      {/* Team Performance */}
      <ChartCard title="Takım Performansı" subtitle="Tamamlanan üretim görevleri (Top 10)" className="mb-6">
        <ResponsiveContainer width="100%" height={Math.max(280, teamData.length * 42)}>
          <BarChart data={teamData} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" width={120} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Üretim Görevleri" fill="#8B5CF6" radius={[0, 6, 6, 0]} animationDuration={1200} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Highlights */}
      {(data.highlights?.longest_task || data.highlights?.most_revised) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {data.highlights?.longest_task && (
            <HighlightCard
              icon={Clock} color="from-orange-500 to-red-500"
              title="En Uzun Süren Görev"
              value={`${data.highlights.longest_task.days} gün`}
              subtitle={data.highlights.longest_task.title}
            />
          )}
          {data.highlights?.most_revised && data.highlights.most_revised.revisions > 0 && (
            <HighlightCard
              icon={RefreshCw} color="from-purple-500 to-pink-500"
              title="En Çok Revize Edilen"
              value={`${data.highlights.most_revised.revisions} revize`}
              subtitle={data.highlights.most_revised.title}
            />
          )}
        </div>
      )}

      {/* Top Performers Podium */}
      {(data.team_performance || []).length >= 3 && (
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" /> En Çok Katkı Sağlayanlar
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {[1, 0, 2].map((idx, position) => {
              const p = data.team_performance[idx];
              if (!p) return null;
              const medals = ['🥈', '🥇', '🥉'];
              const heights = ['h-24', 'h-32', 'h-20'];
              const bgs = ['bg-gradient-to-t from-gray-300 to-gray-200', 'bg-gradient-to-t from-amber-400 to-yellow-300', 'bg-gradient-to-t from-orange-300 to-orange-200'];
              return (
                <div key={p.userId} className="flex flex-col items-center">
                  <div className="text-3xl mb-1">{medals[position]}</div>
                  <div className="font-semibold text-sm text-gray-900 text-center mb-2">{p.name}</div>
                  <div className={`w-full ${heights[position]} ${bgs[position]} rounded-t-lg flex items-center justify-center`}>
                    <div className="text-2xl font-bold text-gray-900">{p.production_completed || 0}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Snapshots Modal */}
      <Dialog open={showSnapshots} onOpenChange={setShowSnapshots}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-emerald-600" /> Snapshots
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Button
              onClick={handleCreateSnapshot}
              disabled={creating}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              data-testid="create-snapshot-btn"
            >
              {creating ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Oluşturuluyor…</>
              ) : (
                <><Camera className="w-4 h-4 mr-2" /> Yeni Snapshot Oluştur</>
              )}
            </Button>

            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Mevcut Snapshots ({snapshots.length})
              </h4>
              {snapshots.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">Henüz snapshot yok. Yeni bir tane oluşturun.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {snapshots.map(s => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200"
                      data-testid={`snapshot-${s.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 flex items-center gap-2">
                          {new Date(s.createdAt).toLocaleString('tr-TR')}
                          {s.appliedTagName && (
                            <span
                              className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-cyan-100 text-cyan-700 border border-cyan-200"
                              title="Kategori filtresi"
                            >
                              {s.appliedTagName}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          Oluşturan: {s.createdByName}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => window.open(s.viewUrl, '_blank')}
                          title="Snapshot'ı yeni pencerede aç"
                          data-testid={`view-snapshot-${s.id}`}
                        >
                          <ExternalLink className="w-4 h-4 text-blue-600" />
                        </Button>
                        {s.pdfDownloadUrl && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => window.open(s.pdfDownloadUrl, '_blank')}
                            title="PDF indir"
                            data-testid={`download-snapshot-${s.id}`}
                          >
                            <Download className="w-4 h-4 text-emerald-600" />
                          </Button>
                        )}
                        {/* Silme — sadece sahibi veya admin görür */}
                        {(user?.role === 'admin' || s.createdBy === user?.id) && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDeleteSnapshot(s.id)}
                            title="Snapshot'ı sil"
                            data-testid={`delete-snapshot-${s.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, suffix, icon: Icon, gradient }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center shadow-md`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <div className="text-3xl font-bold text-gray-900">
        <CountUp end={value || 0} duration={1.5} decimals={Number.isInteger(value) ? 0 : 1} />{suffix}
      </div>
      <div className="text-sm text-gray-600 mt-1">{label}</div>
    </div>
  );
}

function MiniMetric({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3">
      <Icon className={`w-5 h-5 ${color}`} />
      <div>
        <div className="font-bold text-gray-900">{value || 0}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl p-6 shadow-sm border border-gray-100 ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function HighlightCard({ icon: Icon, color, title, value, subtitle }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 bg-gradient-to-br ${color} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="text-sm font-semibold text-gray-700">{title}</div>
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      <div className="text-sm text-gray-500 line-clamp-2">{subtitle}</div>
    </div>
  );
}
